// [claude-code 2026-04-23] S32-T4 Consul Control pixelation corners mounted above modals.
// [claude-code 2026-04-23] Rollback: remove GitHub OAuth callback + update banner mounts
// [claude-code 2026-03-24] Auth gate with init screen, cloud migration, and soft fade-in
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import {
  migrateLocalStorageToCloud,
  isMigrationComplete,
} from "./lib/data-migration";
import { SettingsProvider } from "./contexts/SettingsContext";
import { ThreadProvider } from "./contexts/ThreadContext";
import { ToastProvider } from "./contexts/ToastContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { GatewayProvider } from "./contexts/GatewayContext";
import { FintheonAgentProvider } from "./contexts/FintheonAgentContext";
import { TeamPresenceProvider } from "./contexts/TeamPresenceContext";
import { RiskFlowProvider } from "./contexts/RiskFlowContext";
import { ContextBankProvider } from "./contexts/ContextBankContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { VoiceProvider } from "./contexts/VoiceContext";
import { ProxVoiceProvider } from "./contexts/ProxVoiceContext";
import { VoiceRimFrame } from "./components/voice/VoiceRimFrame";
import { ERProvider } from "./contexts/ERContext";
import { MainLayout } from "./components/layout/MainLayout";
import SplashScreen from "./components/SplashScreen";
import { NotificationContainer } from "./components/NotificationToast";
import { ToastContainer } from "./components/ui/Toast";
import { PreMarketReminder } from "./components/PreMarketReminder";
import { ApiErrorToastBridge } from "./components/ApiErrorToastBridge";
import { AiCreditToastBridge } from "./components/AiCreditToastBridge";
import { DeskPlanPlannerToastBridge } from "./components/DeskPlanPlannerToastBridge";
import { VersionChecker } from "./components/VersionChecker";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SystemStatusProvider } from "./contexts/SystemStatusContext";
import { migrateStorageKeys } from "./lib/storage-migration";
import { AuthShell } from "./components/auth/AuthShell";
import { WelcomeOnboarding } from "./components/onboarding/WelcomeOnboarding";
import { LoadingBootScreen } from "./components/loading/LoadingBootScreen";
import { ConsulControlCorners } from "./components/consul-control/ConsulControlCorners";
import { ProjectXSyncProvider } from "./components/projectx/ProjectXSyncProvider";
import { useConsulControlStatus } from "./hooks/useConsulControlStatus";
import { buildSurfaceCapabilities } from "./lib/surface-capabilities";
import { MainContentOverlayHost } from "./components/layout/MainContentOverlayHost";
import { isAuthBypassAllowed } from "./lib/auth-bypass";
import {
  detectOnboardingSurface,
  fetchOnboardingBundle,
  isSurfaceOnboarded,
} from "./lib/onboarding-v2";
import {
  hasOnboardedHint,
  hasOnboardingRestartRequest,
  markOnboardedHint,
  markOnboardingComplete,
  ONBOARDING_RESTART_EVENT,
} from "./lib/onboarding-lifecycle";

// Run storage migration before any providers read localStorage
migrateStorageKeys();

// [claude-code 2026-04-19] S27-T5 W2c — replaced VoiceBorderPulse with VoiceRimFrame
// (accent-gold rim + transcript ticker + dismiss button; never covers content).

/**
 * Headless init hook — runs backend health check + cloud migration, then signals ready.
 * No UI — the SplashScreen overlay handles visuals.
 * Only runs when `enabled` is true (i.e. authenticated).
 */
function useAppInit(enabled: boolean, onReady: () => void) {
  const { getAccessToken } = useAuth();
  const initStatusRef = useRef<"idle" | "running" | "ready">("idle");

  useEffect(() => {
    if (!enabled) {
      initStatusRef.current = "idle";
      return;
    }
    if (initStatusRef.current === "ready") {
      onReady();
      return;
    }
    if (initStatusRef.current === "running") return;
    initStatusRef.current = "running";

    let cancelled = false;
    const API = import.meta.env.VITE_API_URL || "http://localhost:8080";

    (async () => {
      // Brief pause — session already restored by AuthContext
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled) return;

      // Backend health check
      for (let i = 0; i < 8; i++) {
        try {
          const res = await fetch(`${API}/health`, {
            signal: AbortSignal.timeout(2000),
          });
          if (res.ok) break;
        } catch {
          /* retry */
        }
        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 800));
      }
      if (cancelled) return;

      // Cloud migration (if needed)
      if (!isMigrationComplete()) {
        try {
          const token = await getAccessToken();
          if (token && !cancelled) {
            await migrateLocalStorageToCloud(token);
          }
        } catch (err) {
          console.warn("[Init] Migration skipped:", err);
        }
      }
      if (cancelled) return;

      // Brief settle
      await new Promise((r) => setTimeout(r, 300));
      if (cancelled) return;

      initStatusRef.current = "ready";
      onReady();
    })();

    return () => {
      cancelled = true;
      if (initStatusRef.current === "running") initStatusRef.current = "idle";
    };
  }, [enabled, getAccessToken, onReady]);
}

/** Cold-start detection — sessionStorage clears on quit but persists during background */
function isColdStart(): boolean {
  try {
    if (sessionStorage.getItem("fintheon:session-alive")) return false;
    sessionStorage.setItem("fintheon:session-alive", "1");
    return true;
  } catch {
    return true;
  }
}

/** Pixel-flicker corner indicator — active while Harper is holding the wheel. */
function ConsulControlLayer() {
  const active = useConsulControlStatus();
  return <ConsulControlCorners active={active} />;
}

/** Auth-gated app shell — AuthShell → SplashScreen overlay → MainLayout */
function AuthGate() {
  const { isAuthenticated, isLoading, signIn, user, getAccessToken } =
    useAuth();
  const [initComplete, setInitComplete] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1600 : window.innerWidth,
  );
  const [showSplash] = useState(() => isColdStart());
  const authBypass = isAuthBypassAllowed();
  const canEnterApp = isAuthenticated || authBypass;
  const onboardingSurface = useMemo(() => detectOnboardingSurface(), []);
  const [onboardingChecking, setOnboardingChecking] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(() =>
    hasOnboardedHint(onboardingSurface),
  );
  const [onboardingRestartTick, setOnboardingRestartTick] = useState(0);
  // New web accounts pass through the welcome/download step before the desk.
  const showOnboarding = canEnterApp && !onboardingChecking && !onboardingDone;

  const handleEnterDesk = useCallback(() => {
    markOnboardingComplete(onboardingSurface);
    setOnboardingDone(true);
  }, [onboardingSurface]);
  const surfaceCapabilities = useMemo(
    () => buildSurfaceCapabilities(viewportWidth),
    [viewportWidth],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleRestart = (event: Event) => {
      const detail = (event as CustomEvent<{ surface?: string }>).detail;
      if (detail?.surface && detail.surface !== onboardingSurface) return;
      setOnboardingDone(false);
      setOnboardingChecking(false);
      setOnboardingRestartTick((value) => value + 1);
    };
    window.addEventListener(ONBOARDING_RESTART_EVENT, handleRestart);
    return () =>
      window.removeEventListener(ONBOARDING_RESTART_EVENT, handleRestart);
  }, [onboardingSurface]);

  useEffect(() => {
    let cancelled = false;
    if (!canEnterApp) {
      setOnboardingChecking(false);
      setOnboardingDone(false);
      return;
    }
    if (hasOnboardingRestartRequest(onboardingSurface)) {
      setOnboardingDone(false);
      setOnboardingChecking(false);
      return;
    }
    if (authBypass && !isAuthenticated) {
      setOnboardingDone(true);
      setOnboardingChecking(false);
      return;
    }

    setOnboardingChecking(true);
    fetchOnboardingBundle(getAccessToken)
      .then((bundle) => {
        if (cancelled) return;
        const done = isSurfaceOnboarded(bundle.onboarding, onboardingSurface);
        if (done) markOnboardedHint(onboardingSurface);
        setOnboardingDone(done);
      })
      .catch(() => {
        if (!cancelled) setOnboardingDone(hasOnboardedHint(onboardingSurface));
      })
      .finally(() => {
        if (!cancelled) setOnboardingChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    authBypass,
    canEnterApp,
    getAccessToken,
    isAuthenticated,
    onboardingRestartTick,
    onboardingSurface,
    user?.id,
  ]);

  const handleInitReady = useCallback(() => {
    setInitComplete(true);
  }, []);

  // Run headless init once authenticated
  useAppInit(canEnterApp, handleInitReady);

  if (isLoading) {
    return <LoadingBootScreen phrase="Restoring session" />;
  }

  if (!canEnterApp) {
    return <AuthShell onSignIn={signIn} isLoading={isLoading} />;
  }

  if (onboardingChecking) {
    return <LoadingBootScreen phrase="Restoring desk profile" />;
  }

  // Out-of-the-box: web app is fully usable now (Cloud Hermes). The welcome step
  // lets new accounts grab the desktop .dmg while init runs in the background.
  if (showOnboarding) {
    return (
      <WelcomeOnboarding
        onEnter={handleEnterDesk}
        isDeskReady={initComplete}
        getAccessToken={getAccessToken}
        forceRestart={hasOnboardingRestartRequest(onboardingSurface)}
        surface={onboardingSurface}
        userEmail={user?.email ?? null}
      />
    );
  }

  return (
    <div style={{ opacity: 1 }}>
      {/* Liquid glass splash — cold start only, fades when init completes */}
      {showSplash && <SplashScreen isReady={initComplete} />}
      <SettingsProvider>
        <NotificationsProvider>
          <ToastProvider>
            <GatewayProvider>
              <SystemStatusProvider>
                <FintheonAgentProvider>
                  <TeamPresenceProvider>
                    <RiskFlowProvider>
                      <ContextBankProvider>
                        <ThreadProvider>
                          <VoiceProvider>
                            <ProxVoiceProvider>
                              <ERProvider>
                                <div className="dark">
                                  {surfaceCapabilities.allowVoiceAssistant && (
                                    <VoiceRimFrame />
                                  )}
                                  <style>{`
                  * {
                    scrollbar-width: thin;
                    scrollbar-color: var(--fintheon-accent) var(--fintheon-surface);
                  }

                  *::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                  }

                  *::-webkit-scrollbar-track {
                    background: var(--fintheon-surface);
                  }

                  *::-webkit-scrollbar-thumb {
                    background: var(--fintheon-accent);
                    border-radius: 4px;
                  }

                  *::-webkit-scrollbar-thumb:hover {
                    background: color-mix(in srgb, var(--fintheon-accent) 70%, white);
                  }

                  .scanline-overlay {
                    background: repeating-linear-gradient(
                      0deg,
                      color-mix(in srgb, var(--fintheon-accent) 3%, transparent) 0px,
                      color-mix(in srgb, var(--fintheon-accent) 3%, transparent) 1px,
                      transparent 1px,
                      transparent 2px
                    );
                    pointer-events: none;
                  }
                                `}</style>
                                  <ApiErrorToastBridge />
                                  <AiCreditToastBridge />
                                  <DeskPlanPlannerToastBridge />
                                  <ProjectXSyncProvider />
                                  <VersionChecker />
                                  <MainLayout />
                                  <MainContentOverlayHost />
                                  {surfaceCapabilities.allowVoiceAssistant && (
                                    <ConsulControlLayer />
                                  )}
                                  <NotificationContainer />
                                  <ToastContainer />
                                  <PreMarketReminder />
                                </div>
                              </ERProvider>
                            </ProxVoiceProvider>
                          </VoiceProvider>
                        </ThreadProvider>
                      </ContextBankProvider>
                    </RiskFlowProvider>
                  </TeamPresenceProvider>
                </FintheonAgentProvider>
              </SystemStatusProvider>
            </GatewayProvider>
          </ToastProvider>
        </NotificationsProvider>
      </SettingsProvider>
    </div>
  );
}

/**
 * Fintheon — AuthProvider wraps the entire app so session state is available
 * to both the auth gate and the main application.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AuthGate />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
